import { useEffect, useCallback, useRef } from 'react';

import { refreshTokenAsync } from '@/service/AuthenticationService';
import {
  useAppDispatch,
  useAppSelector,
  selectAccessToken,
  selectTokenExpiry,
  refreshTokenUpdate,
  selectConfig,
} from '@/store';

const MAX_REFRESH_FAILURES = 3;
const DEFAULT_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export function useTokenRefresh(): void {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken);
  const tokenExpiry = useAppSelector(selectTokenExpiry);
  const config = useAppSelector(selectConfig);
  const sessionTimeout = parseFloat(config?.settings.APP_SESSION_TIMEOUT ?? '0');
  const failureCountRef = useRef(0);
  const gaveUpRef = useRef(false);

  const handleTokenRefresh = useCallback(async () => {
    if (!accessToken || gaveUpRef.current) return;

    const isExpired = tokenExpiry !== null && tokenExpiry < Date.now();
    const isUnknownExpiry = tokenExpiry === null;

    if (isExpired || isUnknownExpiry) {
      try {
        const response = await refreshTokenAsync(accessToken);
        const timeout = parseFloat(config?.settings.APP_SESSION_TIMEOUT ?? '0');
        const expiresIn = Date.now() + timeout * 1000;

        dispatch(
          refreshTokenUpdate({
            accessToken: response.refresh_token,
            expiresIn: expiresIn,
          })
        );
        failureCountRef.current = 0;
        gaveUpRef.current = false;
      } catch {
        failureCountRef.current += 1;
        if (failureCountRef.current >= MAX_REFRESH_FAILURES) {
          gaveUpRef.current = true;
        }
      }
    }
  }, [accessToken, tokenExpiry, config?.settings.APP_SESSION_TIMEOUT, dispatch]);

  useEffect(() => {
    if (!accessToken) return;

    void handleTokenRefresh();

    const interval = sessionTimeout > 0 ? sessionTimeout * 1000 : DEFAULT_REFRESH_INTERVAL_MS;
    const intervalId = setInterval(() => {
      void handleTokenRefresh();
    }, interval);

    return () => {
      clearInterval(intervalId);
    };
  }, [accessToken, handleTokenRefresh, sessionTimeout]);
}
